import apiV1 from "../../../services/apiV1";

// ── Projects ──
export const listProjects = (params) =>
  apiV1.get("/projects", { params }).then((r) => r.data);

export const getProject = (id) =>
  apiV1.get(`/projects/${id}`).then((r) => r.data.data);

export const createProject = (payload) =>
  apiV1.post("/projects", payload).then((r) => r.data.data);

export const updateProject = (id, payload) =>
  apiV1.put(`/projects/${id}`, payload).then((r) => r.data.data);

export const deleteProject = (id) =>
  apiV1.delete(`/projects/${id}`).then((r) => r.data);

export const archiveProject = (id) =>
  apiV1.post(`/projects/${id}/archive`).then((r) => r.data.data);

export const duplicateProject = (id) =>
  apiV1.post(`/projects/${id}/duplicate`).then((r) => r.data.data);

export const getDashboard = (id) =>
  apiV1.get(`/projects/${id}/dashboard`).then((r) => r.data.data);

// ── Reference lists (wizard) ──
// Returns { objectives, max, ecommerce }.
export const getObjectivesMeta = () =>
  apiV1.get("/objectives").then((r) => r.data.data);
export const getKpiMetrics = () =>
  apiV1.get("/kpi-metrics").then((r) => r.data.data);
export const getPlatformsList = () =>
  apiV1.get("/platforms").then((r) => r.data.data);
// Business-goal question config, keyed by objective.
export const getGoalConfig = () =>
  apiV1.get("/goal-config").then((r) => r.data.data);

// ── Wizard sub-resources ──
export const setObjectives = (id, objectives, customGoal) =>
  apiV1.put(`/projects/${id}/objectives`, { objectives, customGoal }).then((r) => r.data.data);
export const setPlatforms = (id, platforms) =>
  apiV1.put(`/projects/${id}/platforms`, { platforms }).then((r) => r.data.data);
export const addKpi = (id, payload) =>
  apiV1.post(`/projects/${id}/kpis`, payload).then((r) => r.data.data);
export const deleteKpi = (kpiId) =>
  apiV1.delete(`/kpis/${kpiId}`).then((r) => r.data);
// Business goals (KPI step). setGoals regenerates the derived KPIs server-side.
export const getGoals = (id) =>
  apiV1.get(`/projects/${id}/goals`).then((r) => r.data.data);
export const setGoals = (id, objectives, answers) =>
  apiV1.put(`/projects/${id}/goals`, { objectives, answers }).then((r) => r.data.data);
export const saveBudget = (id, payload) =>
  apiV1.post(`/projects/${id}/budget`, payload).then((r) => r.data.data);
export const saveTimeline = (id, payload) =>
  apiV1.post(`/projects/${id}/timeline`, payload).then((r) => r.data.data);
export const saveContext = (id, payload) =>
  apiV1.post(`/projects/${id}/context`, payload).then((r) => r.data.data);

// ── Strategy ──
export const getStrategy = (id) =>
  apiV1.get(`/projects/${id}/strategy`).then((r) => r.data.data);

// Generation is slow (~90s) — override the default 30s timeout.
export const generateStrategy = (id) =>
  apiV1
    .post(`/projects/${id}/strategy/generate`, {}, { timeout: 180000 })
    .then((r) => r.data.data);

export const approveStrategy = (id) =>
  apiV1.post(`/projects/${id}/strategy/approve`).then((r) => r.data.data);

export const rejectStrategy = (id, reason) =>
  apiV1.post(`/projects/${id}/strategy/reject`, { reason }).then((r) => r.data.data);

// ── Strategy versions ──
export const getStrategyVersions = (id) =>
  apiV1.get(`/projects/${id}/strategy/versions`).then((r) => r.data.data);
export const getStrategyVersion = (versionId) =>
  apiV1.get(`/strategy/version/${versionId}`).then((r) => r.data.data);
export const restoreStrategyVersion = (versionId) =>
  apiV1.post(`/strategy/version/${versionId}/restore`).then((r) => r.data.data);

// ── Notes ──
export const listNotes = (id) =>
  apiV1.get(`/projects/${id}/notes`).then((r) => r.data.data);
export const addNote = (id, payload) =>
  apiV1.post(`/projects/${id}/notes`, payload).then((r) => r.data.data);
export const deleteNote = (noteId) =>
  apiV1.delete(`/notes/${noteId}`).then((r) => r.data);

// ── Files ──
export const listFiles = (id) =>
  apiV1.get(`/projects/${id}/files`).then((r) => r.data.data);
export const uploadFile = (id, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return apiV1
    .post(`/projects/${id}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } })
    .then((r) => r.data.data);
};
export const deleteFile = (fileId) =>
  apiV1.delete(`/files/${fileId}`).then((r) => r.data);
export const downloadFile = (fileId, filename) =>
  apiV1.get(`/files/${fileId}/download`, { responseType: "blob" }).then((r) => {
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "download";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  });

// ── Activity ──
export const getActivity = (id, params) =>
  apiV1.get(`/projects/${id}/activity`, { params }).then((r) => r.data);

// ── Phase 3: Performance ──
// params: { range } or { from, to }, optional { compare: "previous_period" }.
export const getPerformanceSummary = (id, params) =>
  apiV1.get(`/projects/${id}/performance/summary`, { params }).then((r) => r.data.data);
export const getKpiStatus = (id, params) =>
  apiV1.get(`/projects/${id}/kpis/status`, { params }).then((r) => r.data.data);
export const getHealth = (id, params) =>
  apiV1.get(`/projects/${id}/health`, { params }).then((r) => r.data.data);
export const getSyncStatus = (id) =>
  apiV1.get(`/projects/${id}/sync/status`).then((r) => r.data.data);
// Diagnostic: raw result/error per Google/Meta query + platform-vs-stored
// totals. Optional { from, to } checks an exact range (default last 30 days).
export const googleProbe = (id, params) =>
  apiV1.get(`/projects/${id}/google-probe`, { params, timeout: 60000 }).then((r) => r.data.data);
// Sync pulls live ad data — allow it plenty of time.
export const syncProject = (id, lookbackDays) =>
  apiV1.post(`/projects/${id}/sync`, { lookbackDays }, { timeout: 180000 }).then((r) => r.data.data);
export const getStrategyVsActual = (id, params) =>
  apiV1.get(`/projects/${id}/strategy-vs-actual`, { params }).then((r) => r.data.data);
// AI analysis is a Claude call (~30-60s) — override the default timeout.
export const analyzePerformance = (id, body) =>
  apiV1.post(`/projects/${id}/ai/analyze`, body, { timeout: 150000 }).then((r) => r.data.data);

// ── Phase 3: Project AI Chat ──
export const sendChat = (id, body) =>
  apiV1.post(`/projects/${id}/ai/chat`, body, { timeout: 120000 }).then((r) => r.data.data);
export const listConversations = (id) =>
  apiV1.get(`/projects/${id}/ai/conversations`).then((r) => r.data.data);
export const getConversationMessages = (id, conversationId) =>
  apiV1.get(`/projects/${id}/ai/conversations/${conversationId}`).then((r) => r.data.data);
